import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Tag from "../../../../ui-components/molecules/tag/tag";
import { DenimUploadBatch } from "../../../../app/data/invoice-factoring/models";
import { BATCH_STATUS_COLORS, BATCH_STATUS_LABELS, formatDateTime } from "../constants";
import { getBatchNextAction, getProgressPercent, getRemainingCount } from "../utils/nextAction";
import NextActionChip from "../common/nextActionChip";

const columnHelper = createColumnHelper<DenimUploadBatch>();

const getBatchesColumns = () => [
  columnHelper.accessor("batchNumber", {
    id: "batchNumber",
    header: "Batch",
    cell: (info) => `#${info.getValue()}`,
    size: 90,
    minSize: 80,
  }),
  columnHelper.accessor("name", {
    id: "name",
    header: "Name",
    cell: (info) => (
      <div className="xgs-invoice-factoring__two-line">
        <span>{info.getValue()}</span>
        {info.row.original.fileName !== info.getValue() && (
          <span className="xgs-invoice-factoring__muted">{info.row.original.fileName}</span>
        )}
      </div>
    ),
    size: 260,
    minSize: 180,
  }),
  columnHelper.accessor("status", {
    id: "status",
    header: "Status",
    cell: (info) => (
      <Tag mods={{ color: BATCH_STATUS_COLORS[info.getValue()], uppercase: false }}>
        {BATCH_STATUS_LABELS[info.getValue()]}
      </Tag>
    ),
    size: 180,
    minSize: 150,
  }),
  columnHelper.display({
    id: "progress",
    header: "Progress",
    cell: ({ row }) => {
      const batch = row.original;
      const percent = getProgressPercent(batch);
      return (
        <span className={`xgs-invoice-factoring__progress${percent === 100 ? " xgs-invoice-factoring__progress--done" : ""}`}>
          {batch.processedCount} of {batch.totalCount - batch.invalidCount} ({percent}%)
        </span>
      );
    },
    size: 150,
    minSize: 130,
  }),
  columnHelper.display({
    id: "results",
    header: "Results",
    cell: ({ row }) => {
      const batch = row.original;
      return (
        <span className="xgs-invoice-factoring__count-cell">
          <span title="Sent">{batch.succeededCount} sent</span>
          <span title="Already sent before">· {batch.skippedCount} already sent</span>
          <span className={batch.failedCount ? "xgs-invoice-factoring__text--error" : undefined}>
            · {batch.failedCount} failed
          </span>
          {batch.invalidCount > 0 && <span>· {batch.invalidCount} invalid</span>}
          {getRemainingCount(batch) > 0 && <span>· {getRemainingCount(batch)} waiting</span>}
        </span>
      );
    },
    size: 300,
    minSize: 220,
  }),
  columnHelper.display({
    id: "nextAction",
    header: "Next action",
    cell: ({ row }) => <NextActionChip action={getBatchNextAction(row.original)} />,
    size: 220,
    minSize: 180,
  }),
  columnHelper.accessor("createdAt", {
    id: "createdAt",
    header: "Uploaded",
    cell: (info) => (
      <div className="xgs-invoice-factoring__two-line">
        <span>{formatDateTime(info.getValue())}</span>
        <span className="xgs-invoice-factoring__muted">{info.row.original.createdBy}</span>
      </div>
    ),
    size: 220,
    minSize: 180,
  }),
  columnHelper.accessor("completedAt", {
    id: "completedAt",
    header: "Finished",
    cell: (info) => formatDateTime(info.getValue() || info.row.original.stoppedAt),
    size: 170,
    minSize: 150,
  }),
];

export default getBatchesColumns;
